import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * The permission codes collection capture is authorised against.
 *
 * Collections is not a module on the server and has no codes of its own. Every code below is the
 * one the endpoint the sheet actually calls checks — reads over groups, and three separate writes
 * across Loans and Savings. That separation is the point rather than an inconvenience: an officer
 * who may take savings at a meeting is not thereby allowed to take cash collateral, so the two
 * columns are gated apart and a sheet renders with whichever of them the operator can post.
 *
 * Every code is taken from the generated catalogue in `src/lib/permissions.ts`. None of them is a
 * hand-written string, because a code that does not exist there is a bug — it would hide a column
 * from everybody, permanently, and look like a working screen while doing it.
 */
export const COLLECTION_PERMISSIONS = {
  /** Reading the group, its members, and its meeting calendar. Every screen here needs it. */
  groupView: PERMISSIONS.groups.view,
  /** Recording the register. Not required to capture money, but offered alongside it. */
  meetingRecord: PERMISSIONS.groups.meetingRecord,
  /** Reading loan accounts and their live rounds, which is what fills the repayment column. */
  loanView: PERMISSIONS.loans.view,
  repaymentPost: PERMISSIONS.loans.repaymentPost,
  /** Reading savings and security-deposit accounts and their balances. */
  savingsView: PERMISSIONS.savings.view,
  depositPost: PERMISSIONS.savings.depositPost,
  securityDepositTopUp: PERMISSIONS.savings.securityDepositTopUp,
} as const

export interface CollectionPermissions {
  canViewGroups: boolean
  canRecordAttendance: boolean
  canViewLoans: boolean
  canPostRepayment: boolean
  canViewSavings: boolean
  canPostDeposit: boolean
  canTopUpSecurityDeposit: boolean
  /** Whether any money column at all is available. False means the sheet is a read-only view. */
  canPostAnything: boolean
}

/**
 * What this user may do with a collection sheet.
 *
 * Presentation only. The server authorises each of the three writes independently against the live
 * catalogue, so a column shown here is not an open door and a column hidden here is not a closed
 * one — it only spares an operator from keying an amount that was always going to be refused.
 */
export function useCollectionPermissions(): CollectionPermissions {
  const { user } = useAuth()
  const permissions = user?.permissions

  return useMemo(() => {
    const holds = (code: string) => permissions?.some((held) => held === code) ?? false

    const canPostRepayment = holds(COLLECTION_PERMISSIONS.repaymentPost)
    const canPostDeposit = holds(COLLECTION_PERMISSIONS.depositPost)
    const canTopUpSecurityDeposit = holds(COLLECTION_PERMISSIONS.securityDepositTopUp)

    return {
      canViewGroups: holds(COLLECTION_PERMISSIONS.groupView),
      canRecordAttendance: holds(COLLECTION_PERMISSIONS.meetingRecord),
      canViewLoans: holds(COLLECTION_PERMISSIONS.loanView),
      canPostRepayment,
      canViewSavings: holds(COLLECTION_PERMISSIONS.savingsView),
      canPostDeposit,
      canTopUpSecurityDeposit,
      canPostAnything: canPostRepayment || canPostDeposit || canTopUpSecurityDeposit,
    }
  }, [permissions])
}
